import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconTarget() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  );
}

function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function IconChevronDown({ open }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

function IconExternalLink() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
      <polyline points="15 3 21 3 21 9"/>
      <line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  );
}

function IconAlertCircle() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function IconSheets() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/>
    </svg>
  );
}

// ── File badge ────────────────────────────────────────────────────────────────

function FileBadge({ path, matched }) {
  const color   = matched ? 'var(--success)' : 'var(--danger)';
  const bgColor = matched ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      padding: '5px 4px', borderBottom: '1px solid var(--border)',
      fontSize: 12, fontFamily: 'monospace',
    }}>
      <span style={{
        flexShrink: 0, marginTop: 3,
        width: 6, height: 6, borderRadius: '50%',
        background: color, display: 'inline-block',
      }} title={matched ? 'Mapped to feature(s)' : 'No feature mapping found'} />
      <span style={{
        wordBreak: 'break-all', color, background: bgColor,
        padding: '1px 5px', borderRadius: 3, lineHeight: 1.6,
      }}>{path}</span>
    </div>
  );
}

// ── PR summary row (accordion) ────────────────────────────────────────────────

function PrCard({ pr, index }) {
  const [open, setOpen] = useState(false);
  const pi = pr.pr_info || {};
  const hasError = !!pr.error;
  const files    = pr.changed_files || [];
  const features = pr.impacted_features || [];

  const matchedFileSet = new Set(features.flatMap(f => f.matched_files || []));

  const stateIsOpen = pi.state === 'OPEN' || pi.state === 'open' || pi.state === 'opened';
  const stateColor  = stateIsOpen
    ? { bg: 'rgba(34,197,94,0.15)',    fg: 'var(--success)' }
    : { bg: 'rgba(148,163,184,0.15)', fg: 'var(--text-secondary)' };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
      background: hasError ? 'rgba(239,68,68,0.06)' : 'var(--bg-surface)',
    }}>
      {/* Header row */}
      <button
        onClick={() => !hasError && setOpen(o => !o)}
        style={{
          width: '100%', background: 'none', border: 'none',
          cursor: hasError ? 'default' : 'pointer',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 12,
          textAlign: 'left',
        }}
      >
        {/* PR number badge */}
        <span style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
          background: hasError ? 'rgba(239,68,68,0.15)' : 'var(--accent-light)',
          color: hasError ? 'var(--danger)' : 'var(--accent)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700,
        }}>
          {index + 1}
        </span>

        {/* Title / URL */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {hasError ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--danger)', fontSize: 13 }}>
              <IconAlertCircle /> <span style={{ fontWeight: 600 }}>Error</span>
              <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 4, fontSize: 12 }}>
                {pr.pr_url}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{pi.title || pr.pr_url}</span>
              {pi.author && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>by {pi.author}</span>
              )}
              {pi.state && (
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                  background: stateColor.bg, color: stateColor.fg,
                }}>
                  {pi.state}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        {!hasError && (
          <div style={{ display: 'flex', gap: 16, flexShrink: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
            <span><strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{files.length}</strong> files</span>
            <span>
              <strong style={{
                color: features.length > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 700,
              }}>{features.length}</strong> features
            </span>
          </div>
        )}

        {!hasError && <IconChevronDown open={open} />}
      </button>

      {/* Error detail */}
      {hasError && (
        <div style={{ padding: '0 16px 12px', fontSize: 12, color: 'var(--danger)' }}>
          {pr.error}
        </div>
      )}

      {/* Expanded detail */}
      {!hasError && open && (
        <div style={{
          borderTop: '1px solid var(--border)',
          display: 'grid', gridTemplateColumns: '260px 1fr', gap: 0,
        }}>
          {/* Changed files */}
          <div style={{ borderRight: '1px solid var(--border)', padding: '8px 12px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Changed Files ({files.length})
            </div>
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {files.length === 0
                ? <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>None</p>
                : files.map(f => <FileBadge key={f} path={f} matched={matchedFileSet.has(f)} />)
              }
            </div>
          </div>
          {/* Impacted features */}
          <div style={{ padding: '8px 12px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Impacted Features ({features.length})
            </div>
              {features.length === 0 ? (
              <p style={{ fontSize: 12, color: 'var(--success)', margin: 0 }}>No impacted features</p>
            ) : (
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {features.map(f => (
                  <div key={f.feature_pk} style={{
                    fontSize: 12, padding: '4px 0',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex', gap: 8, alignItems: 'baseline',
                  }}>
                    <a href={`/features/${f.feature_pk}`} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {f.feature_id}
                    </a>
                    <span style={{ color: 'var(--text-secondary)' }}>{f.major_feature}</span>
                    {f.sub_feature && <span style={{ color: 'var(--text-muted)' }}>/ {f.sub_feature}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Column definitions for combined features grid ─────────────────────────────

function buildColDefs(isMulti) {
  const cols = [
    {
      field: 'feature_id',
      headerName: 'Feature ID',
      width: 140,
      cellRenderer: ({ value, data }) => (
        <a href={`/features/${data.feature_pk}`} target="_blank" rel="noreferrer"
          style={{ color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          {value} <IconExternalLink />
        </a>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      valueFormatter: ({ value }) => value || '—',
      tooltipValueGetter: ({ value }) => value || '',
      cellStyle: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: 'var(--text-secondary)',
      },
    },
    { field: 'sub_feature',   headerName: 'Sub Feature',   flex: 1, minWidth: 120,
      valueFormatter: ({ value }) => value || '—' },
    { field: 'product_name',  headerName: 'Product',       width: 140,
      valueFormatter: ({ value }) => value || '—' },
    {
      field: 'matched_files',
      headerName: 'Matched Files',
      width: 130,
      valueGetter: ({ data }) => data.matched_files?.length ?? 0,
      cellRenderer: ({ value }) => (
        <span style={{
          background: value > 0 ? 'var(--accent-light)' : 'transparent',
          color: value > 0 ? 'var(--accent)' : 'var(--text-secondary)',
          padding: '2px 8px', borderRadius: 12, fontWeight: 600, fontSize: 12,
        }}>
          {value} file{value !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      field: 'matched_patterns',
      headerName: 'Matched Patterns',
      flex: 2,
      minWidth: 200,
      valueGetter: ({ data }) => (data.matched_patterns || []).join(' | '),
      cellStyle: { fontFamily: 'monospace', fontSize: 12 },
    },
  ];

  if (isMulti) {
    cols.splice(4, 0, {
      field: 'pr_urls',
      headerName: 'Affected PRs',
      width: 120,
      valueGetter: ({ data }) => data.pr_urls?.length ?? 1,
      cellRenderer: ({ value }) => (
        <span style={{
          display: 'inline-block',
          background: value > 1 ? 'rgba(239,68,68,0.15)' : 'var(--accent-light)',
          color: value > 1 ? 'var(--danger)' : 'var(--accent)',
          padding: '2px 10px', borderRadius: 12, fontWeight: 700, fontSize: 12,
        }}>
          {value} PR{value !== 1 ? 's' : ''}
        </span>
      ),
    });
  }

  return cols;
}

const DEFAULT_COL = { sortable: true, resizable: true, filter: true };

// ── Main component ────────────────────────────────────────────────────────────

export default function BlastRadiusPage() {
  const gridRef = useRef(null);

  const [prInput, setPrInput]               = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [result, setResult]                 = useState(null);
  const [filesOpen, setFilesOpen]           = useState(true);
  const [exporting, setExporting]           = useState(false);
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsLink, setSheetsLink]         = useState(null);
  const [jobId, setJobId]                   = useState(null);
  const [jobProgress, setJobProgress]       = useState(null); // { completed, total }

  // Derived
  const prUrls = prInput.split('\n').map(s => s.trim()).filter(Boolean);
  const isMulti = result && result.prs.length > 1;

  // ── Export to Google Sheets ───────────────────────────────────────────────

  async function runSheetsExport(accessToken) {
    setSheetsExporting(true);
    setSheetsLink(null);
    try {
      const { data } = await axios.post('/blast-radius/export-sheets', {
        prs:          result.prs,
        combined:     result.combined,
        access_token: accessToken,
      });
      setSheetsLink(data.spreadsheet_url);
    } catch (err) {
      const msg = 'Sheets export failed: ' + (err.response?.data?.error || err.message || 'Unknown error');
      console.error('[BlastRadius] export-sheets failed:', msg, err);
      setError(msg);
    } finally {
      setSheetsExporting(false);
    }
  }

  const googleSheetsLogin = useGoogleLogin({
    scope: [
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/drive.file',
    ].join(' '),
    onSuccess: tokenResponse => runSheetsExport(tokenResponse.access_token),
    onError: () => setError('Google sign-in was cancelled or failed. Please try again.'),
  });

  // ── Poll bulk job ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!jobId) return;
    const intervalId = setInterval(async () => {
      try {
        const { data } = await axios.get(`/blast-radius/job/${jobId}`);
        setJobProgress({ completed: data.completed, total: data.total });
        if (data.status === 'done') {
          clearInterval(intervalId);
          setResult({ prs: data.prs, combined: data.combined });
          setJobId(null);
          setLoading(false);
        }
      } catch (err) {
        clearInterval(intervalId);
        const msg = err.response?.data?.error || err.message || 'Failed to fetch job status';
        console.error('[BlastRadius] job poll failed:', msg, err);
        setError(msg);
        setJobId(null);
        setLoading(false);
      }
    }, 2000);
    return () => clearInterval(intervalId);
  }, [jobId]);

  // ── Analyze ──────────────────────────────────────────────────────────────

  async function handleAnalyze(e) {
    e.preventDefault();
    if (!prUrls.length) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setJobId(null);
    setJobProgress(null);
    try {
      const { data } = await axios.post('/blast-radius/analyze-bulk', { pr_urls: prUrls });
      setJobId(data.job_id);
      setJobProgress({ completed: 0, total: data.total });
      // loading stays true; polling useEffect takes over from here
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Request failed';
      console.error('[BlastRadius] analyze-bulk failed:', msg, err);
      setError(msg);
      setLoading(false);
    }
  }

  // ── Export CSV ───────────────────────────────────────────────────────────

  async function handleExport() {
    if (!result) return;
    setExporting(true);
    try {
      const resp = await axios.post(
        '/blast-radius/export-multi',
        { pr_urls: result.prs.map(r => r.pr_url) },
        { responseType: 'blob' },
      );
      const disposition = resp.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'blast-radius.csv';
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = 'Export failed: ' + (err.response?.data?.error || err.message || 'Unknown error');
      console.error('[BlastRadius] export-multi failed:', msg, err);
      setError(msg);
    } finally {
      setExporting(false);
    }
  }

  const onGridReady = useCallback(params => { params.api.sizeColumnsToFit(); }, []);

  // ── Derived result data ───────────────────────────────────────────────────

  const combined      = result?.combined || {};
  const allFiles      = combined.all_changed_files || [];
  const allFeatures   = combined.impacted_features || [];
  const prResults     = result?.prs || [];
  const successCount  = combined.successful_prs ?? 0;
  const errorCount    = prResults.filter(r => r.error).length;

  // Files matched to at least one feature across all PRs
  const matchedFileSet = new Set(allFeatures.flatMap(f => f.matched_files || []));

  const colDefs = buildColDefs(isMulti);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconTarget /> Blast Radius Analysis
        </h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
          Enter one or more pull request URLs (one per line) to find which product features are impacted.
        </p>
      </div>

      {/* Input form */}
      <form onSubmit={handleAnalyze}>
        <textarea
          className="form-input"
          rows={prUrls.length > 1 ? Math.min(prUrls.length + 1, 6) : 3}
          style={{
            width: '100%', fontFamily: 'monospace', fontSize: 13,
            resize: 'vertical', boxSizing: 'border-box', marginBottom: 8,
          }}
          placeholder={
            'https://bitbucket.org/workspace/repo/pull-requests/123\n' +
            'https://github.com/owner/repo/pull/456\n' +
            'https://exotel.vegaops.com:64998/NAMESPACE/REPO/-/merge_requests/789'
          }
          value={prInput}
          onChange={e => setPrInput(e.target.value)}
          disabled={loading}
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading || !prUrls.length}
            style={{ minWidth: 140, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {loading
              ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Analyzing…</>
              : <><IconTarget /> Analyze {prUrls.length > 1 ? `${prUrls.length} PRs` : 'PR'}</>
            }
          </button>
          {prUrls.length > 0 && !loading && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {prUrls.length} URL{prUrls.length !== 1 ? 's' : ''} entered
            </span>
          )}
          <div style={{ flex: 1 }} />
          {result && (
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleExport}
                disabled={exporting}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {exporting
                  ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Exporting…</>
                  : <><IconDownload /> Export CSV</>
                }
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setSheetsLink(null); googleSheetsLogin(); }}
                disabled={sheetsExporting}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {sheetsExporting
                  ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Creating…</>
                  : <><IconSheets /> Export to Sheets</>
                }
              </button>
            </>
          )}
        </div>
      </form>

      {/* Progress bar (shown while bulk job is running) */}
      {loading && jobProgress && (
        <div style={{ marginTop: 20 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8,
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
              Analyzing PRs…
            </span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {jobProgress.completed} / {jobProgress.total}
            </span>
          </div>
          <div style={{
            height: 6, borderRadius: 3,
            background: 'var(--border)', overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              background: 'var(--accent)',
              borderRadius: 3,
              width: `${jobProgress.total > 0 ? (jobProgress.completed / jobProgress.total) * 100 : 0}%`,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-error" style={{ marginTop: 16 }}>
          {error}
        </div>
      )}

      {/* Google Sheets link */}
      {sheetsLink && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          marginTop: 12, padding: '10px 16px',
          background: 'rgba(34,197,94,0.08)',
          border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: 8, fontSize: 13,
        }}>
          <IconSheets />
          <span style={{ color: 'var(--text-secondary)' }}>Spreadsheet ready:</span>
          <a
            href={sheetsLink}
            target="_blank"
            rel="noreferrer"
            style={{
              color: 'var(--accent)', fontWeight: 600,
              textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            Open in Google Sheets <IconExternalLink />
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(sheetsLink); }}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 0,
            }}
            title="Copy link"
          >
            Copy link
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={{ marginTop: 24 }}>

          {/* ── Summary strip ───────────────────────────────────────────── */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '8px 32px', alignItems: 'center',
            padding: '14px 20px', borderRadius: 8, marginBottom: 20,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
          }}>
            {[
              { label: 'PRs Analyzed',       value: combined.total_prs,             color: 'var(--accent)' },
              { label: 'Succeeded',           value: successCount,                   color: 'var(--success)' },
              ...(errorCount > 0 ? [{ label: 'Errors', value: errorCount, color: 'var(--danger)' }] : []),
              { label: 'Total Changed Files', value: allFiles.length,                color: 'var(--text-primary)' },
              { label: 'Impacted Features',   value: allFeatures.length,
                color: allFeatures.length > 0 ? 'var(--danger)' : 'var(--success)' },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                <div style={{ fontWeight: 700, fontSize: 22, marginTop: 2, color }}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── Per-PR accordion ─────────────────────────────────────────── */}
          {isMulti && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Per-PR Results
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {prResults.map((pr, i) => (
                  <PrCard key={pr.pr_url + i} pr={pr} index={i} />
                ))}
              </div>
            </div>
          )}

          {/* ── Single-PR error banner (non-multi, all failed) ──────────── */}
          {!isMulti && successCount === 0 && errorCount > 0 && (() => {
            const pr = prResults[0];
            return (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                padding: '14px 20px', borderRadius: 8, marginBottom: 20,
                background: 'rgba(239,68,68,0.06)',
                border: '1px solid rgba(239,68,68,0.3)',
              }}>
                <IconAlertCircle />
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 14, marginBottom: 4 }}>
                    Analysis failed
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--danger)', opacity: 0.85 }}>
                    {pr.error}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    URL: <code style={{ fontFamily: 'monospace' }}>{pr.pr_url}</code>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── Single-PR info banner (non-multi) ───────────────────────── */}
          {!isMulti && successCount > 0 && (() => {
            const pr   = prResults[0];
            const pi   = pr.pr_info || {};
            const stateIsOpen = pi.state === 'OPEN' || pi.state === 'open' || pi.state === 'opened';
            return (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '8px 32px', alignItems: 'center',
                padding: '14px 20px', borderRadius: 8, marginBottom: 20,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
              }}>
                <div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>PR Title</span>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>
                    {pi.url
                      ? <a href={pi.url} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {pi.title || '—'} <IconExternalLink />
                        </a>
                      : pi.title || '—'
                    }
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Author</span>
                  <div style={{ fontWeight: 600, marginTop: 2 }}>{pi.author || '—'}</div>
                </div>
                <div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>State</span>
                  <div style={{ marginTop: 2 }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: stateIsOpen ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                      color: stateIsOpen ? 'var(--success)' : 'var(--text-secondary)',
                    }}>
                      {pi.state || '—'}
                    </span>
                  </div>
                </div>
                {pr.error && (
                  <div style={{ fontSize: 12, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IconAlertCircle /> {pr.error}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Combined results panel ───────────────────────────────────── */}
          {successCount > 0 && (
            <>
              {isMulti && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    Combined Results — All {combined.total_prs} PRs
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>

                {/* Left: Changed files */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    borderBottom: filesOpen ? '1px solid var(--border)' : 'none',
                  }}>
                    <button
                      onClick={() => setFilesOpen(o => !o)}
                      style={{
                        flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                        padding: '12px 16px', display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', fontWeight: 600, fontSize: 14,
                      }}
                    >
                      <span>{isMulti ? 'All ' : ''}Changed Files ({allFiles.length})</span>
                      <IconChevronDown open={filesOpen} />
                    </button>
                    {allFiles.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => { setSheetsLink(null); googleSheetsLogin(); }}
                        disabled={sheetsExporting}
                        title="Export to Google Sheets"
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          margin: '0 8px', padding: '5px 10px', fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {sheetsExporting
                          ? <><span className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} /> Creating…</>
                          : <><IconSheets /> Sheets</>
                        }
                      </button>
                    )}
                  </div>

                  {filesOpen && (
                    <>
                      <div style={{
                        display: 'flex', gap: 12, padding: '6px 16px',
                        borderBottom: '1px solid var(--border)',
                        background: 'var(--bg-elevated)',
                        fontSize: 11, color: 'var(--text-muted)',
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--success)', display: 'inline-block' }} />
                        Mapped ({matchedFileSet.size})
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block' }} />
                          Unmapped ({allFiles.length - matchedFileSet.size})
                        </span>
                      </div>
                      <div style={{ maxHeight: 440, overflowY: 'auto', padding: '6px 12px' }}>
                        {allFiles.length === 0
                          ? <p style={{ color: 'var(--text-muted)', textAlign: 'center', margin: '16px 0' }}>No changed files found.</p>
                          : allFiles.map(f => <FileBadge key={f} path={f} matched={matchedFileSet.has(f)} />)
                        }
                      </div>
                    </>
                  )}
                </div>

                {/* Right: Impacted features */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{
                    padding: '12px 16px', borderBottom: '1px solid var(--border)',
                    fontWeight: 600, fontSize: 14,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: 8,
                  }}>
                    <span style={{ flexShrink: 0 }}>{isMulti ? 'All ' : ''}Impacted Features ({allFeatures.length})</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
                      {allFeatures.length > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, whiteSpace: 'nowrap' }}>
                          Click a Feature ID to open it
                        </span>
                      )}
                      {allFeatures.length > 0 && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => { setSheetsLink(null); googleSheetsLogin(); }}
                          disabled={sheetsExporting}
                          title="Export to Google Sheets"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '5px 10px', fontSize: 12, flexShrink: 0,
                          }}
                        >
                          {sheetsExporting
                            ? <><span className="spinner" style={{ width: 11, height: 11, borderWidth: 2 }} /> Creating…</>
                            : <><IconSheets /> Sheets</>
                          }
                        </button>
                      )}
                    </div>
                  </div>

                  {allFeatures.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
                      <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--success)' }}>No impacted features found</div>
                      <div style={{ fontSize: 13 }}>
                        None of the {allFiles.length} changed file{allFiles.length !== 1 ? 's' : ''} matched
                        any feature's code file patterns.
                      </div>
                    </div>
                  ) : (
                    <div className="ag-theme-alpine" style={{ height: 480, width: '100%' }}>
                      <AgGridReact
                        ref={gridRef}
                        rowData={allFeatures}
                        columnDefs={colDefs}
                        defaultColDef={DEFAULT_COL}
                        tooltipShowDelay={300}
                        onGridReady={onGridReady}
                        suppressCellFocus
                        rowHeight={40}
                        headerHeight={40}
                        animateRows
                        getRowId={({ data }) => String(data.feature_pk)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Idle hint */}
      {!result && !loading && !error && (
        <div style={{
          marginTop: 24,
          border: '2px dashed var(--border)', borderRadius: 8,
          padding: '48px 32px', textAlign: 'center', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.4 }}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block' }}>
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="6"/>
              <circle cx="12" cy="12" r="2"/>
            </svg>
          </div>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Enter PR URLs to begin</div>
          <div style={{ fontSize: 13, maxWidth: 520, margin: '0 auto' }}>
            Paste one or more pull request URLs — one per line — and click <strong>Analyze</strong> to
            see which product features are affected by the changes. Hundreds of PRs can be
            analyzed at once using background processing.
          </div>
          <div style={{ marginTop: 20, fontSize: 12, opacity: 0.7, lineHeight: 2 }}>
            Supported formats:
            <br />
            <code>https://bitbucket.org/&#123;workspace&#125;/&#123;repo&#125;/pull-requests/&#123;id&#125;</code>
            <br />
            <code>https://github.com/&#123;owner&#125;/&#123;repo&#125;/pull/&#123;id&#125;</code>
            <br />
            <code>https://exotel.vegaops.com:64998/&#123;namespace&#125;/&#123;repo&#125;/-/merge_requests/&#123;id&#125;</code>
          </div>
        </div>
      )}
    </div>
  );
}
